import { useContext, useMemo } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import Checked from '@/assets/icons/common/checked.svg';
import { PaymentMethodEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import { getOtherPaymentMethods } from '@/utils/paymentMethods';
import { PaymentMethodRow } from '../PaymentMethodRow/PaymentMethodRow';
import { FontAwesomeIcon } from '../Common/FontAwesomeIcon/FontAwesomeIcon';
import { useTranslate } from '@/hooks/useTranslate';
import { useScreenSurface } from '@/hooks/useScreenSurface';
import { useScrollFade } from '@/hooks/useScrollFade';
import { cn } from '@/utils/cn';
import { trackAndNotifyPaymentMethodSelected } from '@/utils/paymentClickEvents';
import type { ICheckoutContext } from '@/types/context';

export const OtherPaymentMethodsList = () => {
  const context = useContext(CheckoutContext) as ICheckoutContext;
  const {
    paymentMethodsAvailable,
    otherMethodSelected,
    paymentMethodSelected,
    actions: { updateOtherMethodSelected, setPaymentMethodSelected },
    drawer: { closeDrawer },
  } = context;
  const { t } = useTranslate();
  const { isInlineScreen } = useScreenSurface();

  const availablePaymentMethods = useMemo(
    () => getOtherPaymentMethods(paymentMethodsAvailable),
    [paymentMethodsAvailable]
  );

  const { scrollProps } = useScrollFade(availablePaymentMethods.length);

  const handleSelect = (paymentMethod: ExcludeOtherPaymentMethod) => {
    trackAndNotifyPaymentMethodSelected(context, paymentMethod);
    setPaymentMethodSelected(PaymentMethodEnum.OTHER);
    updateOtherMethodSelected(paymentMethod);
    closeDrawer();
  };

  return (
    <div data-testid="other-payment-methods-list" className="flex flex-col gap-2 pt-0">
      {!isInlineScreen ? (
        <div data-testid="other-payment-methods-list-title" className="text-sm font-bold color-card-foreground">
          {t('selectPaymentMethod')}
        </div>
      ) : (
        <div
          data-testid="other-payment-methods-list-back"
          className="flex flex-row items-center gap-0.5 cursor-pointer"
          onClick={closeDrawer}
        >
          <div className="text-sm color-card-foreground font-semibold">
            <FontAwesomeIcon icon="fa-regular fa-arrow-left" className="mr-2" />
            {t('back')}
          </div>
        </div>
      )}

      <div
        {...scrollProps}
        className="scroll-fade scrollbar-hide max-h-90 overflow-y-auto md:max-h-none md:overflow-y-visible"
      >
        {/* Each method is its own separated, rounded card (no shared outer box /
            hairline dividers); the selected one pops with a highlighted border +
            check (matches Figma). */}
        <div className="flex flex-col gap-3">
          {availablePaymentMethods.map((pm) => {
            const value = pm.paymentMethodName as ExcludeOtherPaymentMethod;
            const isSelected = paymentMethodSelected === PaymentMethodEnum.OTHER && otherMethodSelected === value;
            return (
              <div
                key={value}
                data-testid={`other-payment-method-${value}`}
                onClick={() => handleSelect(value)}
                className={cn(
                  'flex flex-row items-center justify-between h-[52px] px-4 box-border cursor-pointer transition-colors rounded-xl border',
                  isSelected ? 'border-selected-border' : 'border-base-border'
                )}
              >
                <PaymentMethodRow paymentMethod={value as PaymentMethodEnum} />
                {isSelected && <img src={Checked} alt="checked" className="h-5 w-5 shrink-0" />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
